import {
    CLUSTER_RADIUS,
    CLUSTER_ZOOM,
    FEATURE_SOURCE,
    GEOJSON_FEATURE_COLLECTION,
    GEOJSON_PROPERTY,
    MAP_LAYER
} from './constants';
import type {
    ClusterStyle,
    Feature,
    GeoJSONSource,
    InitializeClusterLayersArgs,
    LayerSpecification,
    Map,
    UnclusteredPointStyle
} from './types';

const clusterLayers = () => {
    const initialize = ({ clusterStyle, map, unclusteredStyle }: InitializeClusterLayersArgs) => {
        initializeClusterSource(map);

        if (clusterStyle) {
            initializeClusterLayers(map, clusterStyle);
        }

        if (unclusteredStyle) {
            initializeUnclusteredLayers(map, unclusteredStyle);
        }
    };

    const initializeClusterSource = (map: Map) => {
        map.addSource(FEATURE_SOURCE.CLUSTER, {
            cluster: true,
            clusterRadius: CLUSTER_RADIUS,
            data: {
                features: [],
                type: GEOJSON_FEATURE_COLLECTION
            },
            type: 'geojson'
        });
    };

    const initializeClusterLayers = (map: Map, clusterStyle: ClusterStyle) => {
        const { clusterRadiusSteps, clusterSteps, font: clusterFont } = clusterStyle;
        const clusters: LayerSpecification[] = [
            {
                filter: ['has', 'point_count'],
                id: MAP_LAYER.CLUSTER,
                maxzoom: CLUSTER_ZOOM.max,
                minzoom: CLUSTER_ZOOM.min,
                paint: {
                    'circle-color': ['step', ['get', 'point_count'], ...clusterSteps],
                    'circle-radius': ['step', ['get', 'point_count'], ...clusterRadiusSteps]
                },
                source: FEATURE_SOURCE.CLUSTER,
                type: 'circle'
            },
            {
                filter: ['has', 'point_count'],
                id: MAP_LAYER.CLUSTER_COUNT,
                layout: {
                    'text-field': ['get', 'point_count'],
                    'text-font': clusterFont.type,
                    'text-size': clusterFont.size
                },
                maxzoom: CLUSTER_ZOOM.max,
                minzoom: CLUSTER_ZOOM.min,
                paint: { 'text-color': clusterFont.color },
                source: FEATURE_SOURCE.CLUSTER,
                type: 'symbol'
            }
        ];

        clusters.forEach(clusterLayer => map.addLayer(clusterLayer));
    };

    const initializeUnclusteredLayers = (map: Map, unclusteredStyle: UnclusteredPointStyle) => {
        const { color, font, radius } = unclusteredStyle;
        const { color: fontColor, size: fontSize, type: fontType } = font;
        const unclusteredLayers: LayerSpecification[] = [
            {
                filter: ['!', ['has', 'point_count']],
                id: MAP_LAYER.UNCLUSTERED_POINT,
                maxzoom: CLUSTER_ZOOM.max,
                minzoom: CLUSTER_ZOOM.min,
                paint: {
                    'circle-color': color,
                    'circle-radius': radius
                },
                source: FEATURE_SOURCE.CLUSTER,
                type: 'circle'
            },
            {
                filter: ['!', ['has', 'point_count']],
                id: MAP_LAYER.UNCLUSTERED_COUNT,
                layout: {
                    'text-field': '1',
                    'text-font': fontType,
                    'text-size': fontSize
                },
                maxzoom: CLUSTER_ZOOM.max,
                minzoom: CLUSTER_ZOOM.min,
                paint: { 'text-color': fontColor },
                source: FEATURE_SOURCE.CLUSTER,
                type: 'symbol'
            }
        ];

        unclusteredLayers.forEach(unclusteredLayer => map.addLayer(unclusteredLayer));
    };

    const updateFeatures = (map: Map, features: Feature[]) => {
        const clusterSource = map.getSource(FEATURE_SOURCE.CLUSTER) as GeoJSONSource;

        if (clusterSource) {
            const clusterFeatures = features.filter(
                feature =>
                    feature.properties?.[GEOJSON_PROPERTY.REFERENCED_BOUNDARY_ID] !== undefined
            );

            clusterSource.setData({
                features: clusterFeatures,
                type: GEOJSON_FEATURE_COLLECTION
            });
        }
    };

    return {
        initialize,
        updateFeatures
    };
};

export default clusterLayers;
